import Select from "react-select";
import {
  IGlobalProduct,
  IMpnInformation,
  ISkuInformation,
  ITcc,
} from "../../../../types/data.interface";
import {
  formatLifecycleIntoOption,
  lifecycleStatusOptions,
} from "../../../../helpers";
import { useSupplierCountryContext } from "../../../../context/SupplierCountryContext";
import useTpsType from "../../../../hooks/useTpsType";
import OptionFormatter from "../../../../components/OptionFormatter";
import {
  formatTpsTypeIntoOption,
  getTpsTypes,
} from "../../../../helpers/tps-type.helper";
import {
  formatSupplierCountryIntoOption,
  getSupplierCountries,
} from "../../../../helpers/supplier-country.helper";
import { Tooltip } from "react-tooltip";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCircleInfo } from "@fortawesome/free-solid-svg-icons";
import { useLinePlanningStatusConditionsContext } from "context/LinePlanningStatusConditionsContext";
import {
  LIFECYCLE_STATUS_LABEL,
  PRODUCTION_STATUS_LABEL,
  SALES_STATUS_LABEL,
} from "constants/labels";
import { useCreateMpnStore } from "../../context/create/CreateMpnContext";
import { CreateMpnActions } from "../../states/create";
import { CreateMpnActionTypes } from "../../states/create/data";
import { calculateLinePlanningStatus } from "helpers/line-planning-status.helper";
import AuthContext from "../../../../context/AuthContext";
import { useContext, useEffect } from "react";
import { GlobalContext } from "../../../../context/GlobalContextProvider";
import { formatIntoOption } from "../../../../helpers/formatIntoOption";
import { Predicates } from "../../../../libraries/predicates/predicates";

export type InformationProps = {
  requestRevision: string;
  selectedGP: IGlobalProduct | null;
  selectedTcc: ITcc | null;
  skuData: ISkuInformation;
  setSkuData: (info: ISkuInformation) => void;
  setUnsavedChangesToTrue: () => void;
};


const Information = () => {
  const { state, dispatch } = useCreateMpnStore();
  const authContext = useContext(AuthContext);

  const hasCanChangeFusionSupplierPermission = authContext?.hasPermission("api.can_change_fusion_suupplier")

  const { data: vendors, isLoading: isVendorsLoading } =
    useSupplierCountryContext();
  const { data: tpsTypes, isLoading: isTpsTypesLoading } = useTpsType({});
  const {
    data: linePlanningStatusConditions,
    isLoading: isLinePlanningStatusConditionsLoading,
  } = useLinePlanningStatusConditionsContext();

  const {
    fusionSuppliers: {
      data: fusionSupplierData,
      isLoading: isLoadingFusionSupplier,
    },
  } = useContext(GlobalContext);
  const fusionSupplierOptions = fusionSupplierData.map((f: any) =>
    formatIntoOption<any>(f, { keys: ["supplier_code", "supplier_name"] }),
  );

  const formatGiftBoxOption = (data: string) => {
    return { label: data === "Y" ? "Yes" : "No", value: data ? data : "N" };
  };
  const formatProductUseOption = (data: string) => {
    return data
      ? { label: data === "line" ? "Line" : "Promo", value: data }
      : null;
  };
  const formatTpsProductOption = (data: string) => {
    return { label: data === "Y" ? "Yes" : "No", value: data ? data : "N" };
  };

  const handleSkuDataChange = (key: keyof IMpnInformation, e: any, fallBack=null) => {
    if (e) {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN_DATA, {
          ...state.mpnData,
          [key]: e.hasOwnProperty("target") ? e.target.value : e.value,
        }),
      );
    } else {
       dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN_DATA, {
          ...state.mpnData,
          [key]: fallBack,
        }),
      );
    }
    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_UNSAVED_CHANGES,
        true,
      ),
    );


  };

  const handleTpsProductChange = (e: any) => {
    if (e) {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN_DATA, {
          ...state.mpnData,
          tps_product: e.value,
          tps_loc_id: null,
          tps_type_id: null,
        }),
      );
    } else {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_MPN_DATA, {
          ...state.mpnData,
          tps_product: "N",
          tps_loc_id: null,
          tps_type_id: null,
        }),
      );
    }
    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_UNSAVED_CHANGES,
        true,
      ),
    );
  };

  const handleFusionSupplier = (e: any) => {
      dispatch(
        CreateMpnActions.setStateValue(CreateMpnActionTypes.SET_FUSION_SUPPLIER_ID, e?.value ? e.value : e
        ),
      );

    dispatch(
      CreateMpnActions.setStateValue(
        CreateMpnActionTypes.SET_UNSAVED_CHANGES,
        true,
      ),
    );
  };

  return (
    <>
      {/*<HierarchyInfoSection tcc={selectedTcc} gp={selectedGP} />*/}

      <div className="card-header my-2 rounded d-flex justify-content-between">
        <h6 className="m-0 text-primary fw-bold">MPN Information</h6>
      </div>

      <div className="row align-items-end mt-2">
        <div className="col-lg-8">
          <label className="form-label">
            MPN Description <span className="red-text fw-bold">*</span>
          </label>
          <div className="d-flex align-items-center">
            <input
              onChange={(e: any) => handleSkuDataChange("name", e)}
              value={state.mpnData.name}
              type="text"
              className="form-control d-inline"
            />
          </div>
        </div>
        <div className="col-4">
          <Tooltip
            id="project-id"
            className="tooltip-text-box"
            place="right"
            noArrow={true}
          />
          <label className="form-label">Project ID</label>
          <span
            className="tooltip-span"
            data-tooltip-id="project-id"
            data-tooltip-html={`Local reference number for the MPN request (ex. RX number in Workfront, TBD or NP number for the Regions)`}
          >
            <FontAwesomeIcon icon={faCircleInfo} className="tooltip-icon" />
          </span>
          <div className="d-flex align-items-center">
            <input
              onChange={(e: any) => handleSkuDataChange("project_id", e)}
              value={state.mpnData.project_id}
              maxLength={8}
              type="text"
              className="form-control d-inline"
            />
          </div>
        </div>
      </div>

      <div className="row align-items-end mt-2">
        <div className="col-md-4 form-group">
          <Tooltip
            id="parents-production-status"
            className="tooltip-text-box"
            place="right"
            noArrow={true}
          />
          <label className="form-label">
            {PRODUCTION_STATUS_LABEL}
            {Number(state.mpnRequest.revision) > 0 && (
              <span className="red-text fw-bold">*</span>
            )}
          </label>
          {Number(state.mpnRequest.revision) > 0 && (
            <span
              className="tooltip-span"
              data-tooltip-id="parents-production-status"
              // data-tooltip-html={`<b>Global Product:</b> ${selectedGP?.production_lifecycle_status ? selectedGP.production_lifecycle_status : "Not defined yet"} </br> <b>TCC:</b> ${selectedTcc?.production_lifecycle_status ? selectedTcc.production_lifecycle_status : "Not defined yet"}`}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="tooltip-icon" />
            </span>
          )}
          <Select
            onChange={(e: any) =>
              handleSkuDataChange("production_lifecycle_status", e)
            }
            formatOptionLabel={OptionFormatter}
            value={formatLifecycleIntoOption(
              state.mpnData.production_lifecycle_status,
            )}
            options={lifecycleStatusOptions}
            classNamePrefix="react-select"
            placeholder=""
            // components={{
            //   IndicatorSeparator: () => null,
            //   DropdownIndicator: (props) =>
            //     state.mpnRequest.revision === "0" ? null : (
            //       <components.DropdownIndicator {...props} />
            //     ),
            // }}
            isDisabled={
              state.mpnRequest.revision === "" ||
              state.mpnRequest.revision === "0"
              // state.itemNumber.selectedGP?.production_lifecycle_status === "Inactive" ||
              // state.itemNumber?.production_lifecycle_status === "Inactive"
            }
          />
        </div>

        <div className="col-md-4 form-group">
          <Tooltip
            id="parents-sales-status"
            className="tooltip-text-box"
            place="right"
            noArrow={true}
          />
          <label className="form-label">
            {SALES_STATUS_LABEL}
            {Number(state.mpnRequest.revision) > 0 && (
              <span className="red-text fw-bold">*</span>
            )}
          </label>
          {Number(state.mpnRequest.revision) > 0 && (
            <span
              className="tooltip-span"
              data-tooltip-id="parents-sales-status"
              // data-tooltip-html={`<b>Global Product:</b> ${selectedGP?.sales_lifecycle_status ? selectedGP.sales_lifecycle_status : "Not defined yet"} </br> <b>TCC:</b> ${selectedTcc?.sales_lifecycle_status ? selectedTcc.sales_lifecycle_status : "Not defined yet"}`}
            >
              <FontAwesomeIcon icon={faCircleInfo} className="tooltip-icon" />
            </span>
          )}
          <Select
            onChange={(e: any) =>
              handleSkuDataChange("sales_lifecycle_status", e)
            }
            formatOptionLabel={OptionFormatter}
            value={formatLifecycleIntoOption(
              state.mpnData.sales_lifecycle_status,
            )}
            options={lifecycleStatusOptions}
            classNamePrefix="react-select"
            placeholder=""
            // components={{
            //   IndicatorSeparator: () => null,
            //   DropdownIndicator: (props) =>
            //     state.mpnRequest.revision === "0" ? null : (
            //       <components.DropdownIndicator {...props} />
            //     ),
            // }}
            isDisabled={
              state.mpnRequest.revision === "" ||
              state.mpnRequest.revision === "0"
              // selectedGP?.sales_lifecycle_status === "Inactive" ||
              // selectedTcc?.sales_lifecycle_status === "Inactive"
            }
          />
        </div>

        <div className="col-md-4 form-group">
          <Tooltip
            id="line-status"
            className="tooltip-text-box"
            place="right"
            noArrow={true}
          />
          <label className="form-label">{LIFECYCLE_STATUS_LABEL}</label>
          <span
            className="tooltip-span"
            data-tooltip-id="line-status"
            data-tooltip-html={`The ${LIFECYCLE_STATUS_LABEL.toLowerCase()} results from the combination of <br/> the ${PRODUCTION_STATUS_LABEL.toLowerCase()} and ${SALES_STATUS_LABEL.toLowerCase()}.`}
          >
            <FontAwesomeIcon icon={faCircleInfo} className="tooltip-icon" />
          </span>
          <input
            value={calculateLinePlanningStatus(
              linePlanningStatusConditions,
              state.mpnData.production_lifecycle_status,
              state.mpnData.sales_lifecycle_status,
            )}
            type="text"
            className="form-control"
            disabled={true}
          />
        </div>
      </div>
      <div className="row align-items-end mt-2">
        <div className="col-lg-4 ">
          <label className="form-label">
            Release date <span className="red-text fw-bold">*</span>
          </label>
          <div className="d-flex align-items-center">
            <input
              onChange={(e: any) => handleSkuDataChange("reldt", e)}
              value={state.mpnData.reldt}
              type="date"
              min="1993-01-01"
              className="form-control d-inline"
            />
          </div>
        </div>

        <div className="col-lg-4 ">
          <label className="form-label">
            Product Use <span className="red-text fw-bold">*</span>
          </label>
          <Select
            options={[
              { label: "Line", value: "line" },
              { label: "Promo", value: "promo" },
            ]}
            onChange={(e: any) => handleSkuDataChange("product_use", e)}
            value={formatProductUseOption(state.mpnData.product_use)}
            classNamePrefix="react-select"
            placeholder=""
            isClearable
            components={{
              IndicatorSeparator: () => null,
              LoadingIndicator: () => null,
            }}
          />
        </div>

        <div className="col-lg-4 ">
          <label className="form-label">
            Gift Box <span className="red-text fw-bold">*</span>
          </label>
          <Select
            options={[
              { label: "No", value: "N" },
              { label: "Yes", value: "Y" },
            ]}
            onChange={(e: any) => handleSkuDataChange("giftbox", e)}
            value={formatGiftBoxOption(state.mpnData.giftbox)}
            classNamePrefix="react-select"
            placeholder=""
            isClearable
            components={{
              IndicatorSeparator: () => null,
              LoadingIndicator: () => null,
            }}
          />
        </div>
      </div>

      <div className="row align-items-end mt-2">
        <div className="col-lg-4 ">
          <label className="form-label">
            TPS Product <span className="red-text fw-bold">*</span>
          </label>
          <Select
            options={[
              { label: "No", value: "N" },
              { label: "Yes", value: "Y" },
            ]}
            onChange={handleTpsProductChange}
            value={formatTpsProductOption(state.mpnData.tps_product)}
            classNamePrefix="react-select"
            placeholder=""
            isClearable
            components={{
              IndicatorSeparator: () => null,
              LoadingIndicator: () => null,
            }}
          />
        </div>
        <>
          <div className="col-4">
            <label className="form-label">
              Vendor
              {state.mpnData.tps_product === "Y" && (
                <span className="red-text fw-bold"> *</span>
              )}
            </label>
            <Select
              name="tpsVendor"
              value={
                state.mpnData.tps_loc_id && state.mpnData.tps_loc_id.id !== "0"
                  ? formatSupplierCountryIntoOption(state.mpnData.tps_loc_id)
                  : null
              }
              onChange={(e: any) => handleSkuDataChange("tps_loc_id", e)}
              options={getSupplierCountries(vendors)}
              formatOptionLabel={OptionFormatter}
              isLoading={isVendorsLoading}
              isDisabled={state.mpnData.tps_product === "N"}
              isClearable
              isSearchable
              placeholder="(min 3 digits)"
              classNamePrefix="react-select"
            />
          </div>

          <div className="col-4">
            <label className="form-label">
              TPS Type
              {state.mpnData.tps_product === "Y" && (
                <span className="red-text fw-bold"> *</span>
              )}
            </label>
            <Select
              name="tpsType"
              value={
                state.mpnData.tps_type_id &&
                state.mpnData.tps_type_id.res_pis_type_id !== "0"
                  ? formatTpsTypeIntoOption(state.mpnData.tps_type_id)
                  : null
              }
              onChange={(e: any) => handleSkuDataChange("tps_type_id", e)}
              options={getTpsTypes(tpsTypes)}
              formatOptionLabel={OptionFormatter}
              isLoading={isTpsTypesLoading}
              isDisabled={state.mpnData.tps_product === "N"}
              isClearable
              isSearchable
              placeholder=""
              classNamePrefix="react-select"
            />
          </div>
        </>
      </div>

      {hasCanChangeFusionSupplierPermission &&
      <div className="row align-items-end mt-2">
        <div className="col-lg-4 ">
          <label className="form-label">
            Fusion Supplier <span className="red-text fw-bold">*</span>
          </label>
          <Select
            //@ts-ignore
            options={fusionSupplierOptions}
            onChange={handleFusionSupplier}
            //@ts-ignore
            value={
              Predicates.isNotNullAndNotUndefinedAndNotEmpty(
                state.fusionSupplier?.id,
              )
                ? formatIntoOption<any>(state.fusionSupplier, {
                    keys: ["supplier_code", "supplier_name"],
                  })
                : ""
            }
            classNamePrefix="react-select"
            placeholder="Select Fusion supplier"
            isClearable
            isLoading={isLoadingFusionSupplier}
            isDisabled={!hasCanChangeFusionSupplierPermission}
          />
        </div>
      </div>
      }

      <div className="row align-items-end mt-2">
        <div className="col-4">
          <label className="form-label">Product Gross Weight (g)</label>
          <div className="d-flex align-items-center">
            <input
              onChange={(e: any) => handleSkuDataChange("pgrwgt", e)}
              value={state.mpnData.pgrwgt}
              maxLength={6}
              type="text"
              className="form-control d-inline"
              disabled={false}
            />
          </div>
        </div>

        <div className="col-4">
          <label className="form-label">Product Net Weight (g)</label>
          <div className="d-flex align-items-center">
            <input
              onChange={(e: any) => handleSkuDataChange("pntwgt", e)}
              value={state.mpnData.pntwgt}
              maxLength={6}
              type="text"
              className="form-control d-inline"
              disabled={false}
            />
          </div>
        </div>

        <div className="col-4">
          <div className="pb-1">
            <div className="label-3 bottom-border">
              <label className="form-label">
                <strong> Product Dimension</strong>
              </label>
            </div>
          </div>
          <div className="d-flex">
            <div className="col-4 label-3 pl-0">
              <label className="form-label">L (mm)</label>
              <div className="d-flex align-items-center">
                <input
                  onChange={(e: any) => handleSkuDataChange("length", e)}
                  value={state.mpnData.length}
                  maxLength={6}
                  type="text"
                  className="form-control d-inline"
                  disabled={false}
                />
              </div>
            </div>
            <div className="col-4 label-3">
              <label className="form-label">W (mm)</label>
              <div className="d-flex align-items-center">
                <input
                  onChange={(e: any) => handleSkuDataChange("width", e)}
                  value={state.mpnData.width}
                  maxLength={6}
                  type="text"
                  className="form-control d-inline"
                  disabled={false}
                />
              </div>
            </div>
            <div className="col-4 label-3 pr-0">
              <label className="form-label">H (mm)</label>
              <div className="d-flex align-items-center">
                <input
                  onChange={(e: any) => handleSkuDataChange("height", e)}
                  value={state.mpnData.height}
                  maxLength={6}
                  type="text"
                  className="form-control d-inline"
                  disabled={false}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="col mt-2">
        <label className="form-label">Comment</label>
        <textarea
          value={state.mpnData.comments}
          rows={3}
          className="form-control mb-2"
          onChange={(e: any) => handleSkuDataChange("comments", e)}
        />
      </div>
    </>
  );
};

export default Information;
